
import React from 'react';
import Button from '../../../components/base/Button';

export default function DistributionSection() {
  const distributionChannels = [
    {
      icon: 'ri-hospital-line',
      title: 'Primary Health Centers',
      description: 'Available at 150+ PHCs across India for easy accessibility in rural and urban areas.',
      availability: '150+ Centers',
      coverage: 'Pan-India',
      image: 'https://public.readdy.ai/ai/img_res/1b6f6ab76400a6434acdb9e586d33e73.jpg'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'ASHA Workers',
      description: 'Trained community health workers providing door-to-door delivery and health guidance.',
      availability: '2,500+ Workers',
      coverage: '25 States',
      image: 'https://readdy.ai/api/search-image?query=Professional%20ASHA%20worker%20in%20uniform%20delivering%20healthcare%20products%20to%20rural%20family%2C%20female%20community%20health%20worker%20with%20medical%20bag%20visiting%20village%20home%2C%20healthcare%20outreach%20and%20door-to-door%20service%2C%20caring%20healthcare%20professional%20in%20Indian%20village%20setting&width=300&height=200&seq=asha-distribution&orientation=landscape'
    },
    {
      icon: 'ri-shopping-bag-line',
      title: 'Online Store',
      description: 'Direct-to-consumer sales with fast delivery and comprehensive product information.',
      availability: '24/7 Available',
      coverage: 'Nationwide',
      image: 'https://readdy.ai/api/search-image?query=Modern%20e-commerce%20packaging%20and%20delivery%20for%20oxygen%20health%20products%2C%20online%20shopping%20experience%20with%20healthcare%20supplements%2C%20professional%20product%20delivery%20box%20with%20oxygen%20branding%2C%20digital%20commerce%20for%20medical%20supplements%20with%20teal%20packaging&width=300&height=200&seq=online-distribution&orientation=landscape'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Find Your Nearest Center',
      description: 'Locate PHCs or ASHA workers in your area using our distribution map.',
      icon: 'ri-map-pin-line'
    },
    {
      number: '2',
      title: 'Consult & Select',
      description: 'Get personalized recommendations based on your health needs.',
      icon: 'ri-stethoscope-line'
    },
    {
      number: '3',
      title: 'Purchase & Receive',
      description: 'Buy affordable OxyKits or individual products with guidance.',
      icon: 'ri-shopping-cart-line'
    },
    {
      number: '4',
      title: 'Follow-up Support',
      description: 'Ongoing health monitoring and product usage guidance.',
      icon: 'ri-heart-pulse-line'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Accessible Healthcare Distribution
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Our comprehensive distribution network ensures oxygen supplementation products 
            reach every community through trusted healthcare channels.
          </p>
        </div>

        {/* Distribution Channels */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {distributionChannels.map((channel, index) => (
            <div 
              key={index}
              className="group bg-[#FAFAFA] rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
            >
              {/* Image */}
              <div className="aspect-[3/2] overflow-hidden">
                <img 
                  src={channel.image} 
                  alt={channel.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Content */}
              <div className="p-6 bg-white">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                    <i className={`${channel.icon} text-xl text-[#12B6B6]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#111111]">{channel.title}</h3>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">{channel.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="font-bold text-[#12B6B6] text-sm">{channel.availability}</div>
                    <div className="text-xs text-gray-600">Available</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="font-bold text-[#12B6B6] text-sm">{channel.coverage}</div>
                    <div className="text-xs text-gray-600">Coverage</div>
                  </div>
                </div>

                <Button variant="outline" size="sm" className="w-full">
                  Find Locations
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-gradient-to-br from-[#12B6B6]/5 to-[#12B6B6]/10 rounded-3xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-[#111111] text-center mb-12">
            How Our Distribution Works
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-[#12B6B6] text-white rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
                    {step.number}
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <i className={`${step.icon} text-[#12B6B6]`}></i>
                  </div>
                </div>
                
                <h4 className="text-lg font-bold text-[#111111] mb-3">{step.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-[#111111] mb-6">
                Find Products Near You
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Use our interactive map to locate the nearest PHC or ASHA worker 
                in your area. Get real-time availability and contact information.
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-3">
                  <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                  <span className="text-gray-700">Real-time location tracking</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-time-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                  <span className="text-gray-700">Operating hours & availability</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                  <span className="text-gray-700">Direct contact information</span>
                </div>
              </div>

              <Button variant="primary" size="lg">
                Open Distribution Map
              </Button>
            </div>

            <div className="aspect-video bg-gray-100 rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224345.8397151251!2d77.04417565!3d28.527252!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x52c2b7494e204dce!2sNew%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Distribution Centers Map"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
